import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Search as SearchIcon,
  Campaign as CampaignIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { bulkImportAPI } from '../services/leadGenAPI';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 400,
    },
  },
};

const CampaignSelector = ({ 
  accountId, 
  selectedCampaigns = [], 
  onChange, 
  multiple = true,
  disabled = false,
  required = false,
  label = "Target Campaigns",
  helperText = "Select campaigns to assign the suppression list to"
}) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (accountId) {
      loadCampaigns();
    }
  }, [accountId]);

  const loadCampaigns = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await bulkImportAPI.getAvailableCampaigns(accountId);
      
      // Handle different response structures
      const campaignsData = response.campaigns || response.results || response || [];
      
      setCampaigns(Array.isArray(campaignsData) ? campaignsData : []);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
      setError('Failed to load campaigns. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event) => {
    const value = event.target.value;
    onChange(value);
  };

  const filteredCampaigns = campaigns.filter(campaign =>
    campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    campaign.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'completed': return 'info';
      case 'draft': return 'default';
      default: return 'default';
    }
  };

  const renderSelectedValue = (selected) => {
    if (!multiple) {
      const campaign = campaigns.find(c => c.id === selected);
      return campaign ? campaign.name : '';
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selected.map((value) => {
          const campaign = campaigns.find(c => c.id === value);
          return campaign ? (
            <Chip
              key={value}
              label={campaign.name}
              size="small"
              color="primary"
              variant="outlined"
              onDelete={() => {
                const newSelected = selected.filter(id => id !== value);
                onChange(newSelected);
              }}
            />
          ) : null;
        })}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={2}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading campaigns...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <FormControl fullWidth required={required} disabled={disabled}>
        <InputLabel>{label}</InputLabel>
        <Select
          multiple={multiple}
          value={selectedCampaigns}
          onChange={handleChange}
          input={<OutlinedInput label={label} />}
          renderValue={renderSelectedValue}
          MenuProps={MenuProps}
        >
          {/* Search field */}
          <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              size="small"
              placeholder="Search campaigns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </Box>

          {/* Campaign list */}
          {filteredCampaigns.length === 0 ? (
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                {searchTerm ? 'No campaigns found matching your search' : 'No campaigns available'}
              </Typography>
            </MenuItem>
          ) : (
            filteredCampaigns.map((campaign) => (
              <MenuItem key={campaign.id} value={campaign.id}>
                {multiple && (
                  <Checkbox 
                    checked={selectedCampaigns.indexOf(campaign.id) > -1}
                    color="primary"
                  />
                )}
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" color="text.primary">
                        {campaign.name}
                      </Typography>
                      <Chip
                        label={campaign.status}
                        size="small"
                        color={getStatusColor(campaign.status)}
                        variant="outlined"
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {campaign.description || 'No description'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {campaign.suppression_lists_count || 0} suppression lists • Created {new Date(campaign.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  }
                />
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
      
      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {helperText}
        </Typography>
      )}

      {/* Selected campaigns summary */}
      {selectedCampaigns.length > 0 && (
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
            <Typography variant="subtitle2" color="text.primary" gutterBottom>
              Selected Campaigns ({selectedCampaigns.length})
            </Typography>
            <Grid container spacing={1}>
              {selectedCampaigns.map((campaignId) => {
                const campaign = campaigns.find(c => c.id === campaignId);
                return campaign ? (
                  <Grid item xs={12} sm={6} key={campaignId}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CampaignIcon color="primary" fontSize="small" />
                      <Typography variant="body2" color="text.primary">
                        {campaign.name}
                      </Typography>
                      <Chip
                        label={campaign.status}
                        size="small"
                        color={getStatusColor(campaign.status)}
                        variant="outlined"
                      />
                    </Box>
                  </Grid>
                ) : null;
              })}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default CampaignSelector; 